/**
 * 個人資料變更頁面
 * My Profile Page - Allow users to view and update their own profile
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { usePermission } from '../hooks/usePermission';
import { useTransactionToken } from '../hooks/useTransactionToken';
import { useMessage } from '../contexts/MessageContext';
import { useEmbed, usePageExit } from '../contexts/EmbedContext';
import TransactionExtendDialog from '../components/TransactionExtendDialog';
import FunctionPageHeader from '../components/FunctionPageHeader';
import MyAiUsagePanel from '../components/MyAiUsagePanel';
import LineBindingSection from '../components/LineBindingSection';
import { logRead, logUpdate } from '../utils/userLogHelper';
import { getMyProfile, updateMyProfile } from '../services/userService';
import { UserDetail } from '../services/userService';
import { getOrganization } from '../services/organizationService';
import '../styles/MyProfilePage.css';

const MyProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { embedded, prefill, setDirty } = useEmbed();   // §5.8/5.9 內嵌情境
  const pageExit = usePageExit();
  const { showSuccess, showError, showApiError } = useMessage();
  const hasInitialized = useRef(false);
  const prefillApplied = useRef(false);
  // AI 對話帶入的欄位（顯示「由對話帶入，請確認」+ 主題色外框·§5.9）
  const [prefilledFields, setPrefilledFields] = useState<string[]>([]);

  const { hasPermission, loading: permissionLoading } = usePermission();
  const {
    txnToken,
    remainingSeconds,
    showExtendPrompt,
    handleExtendResponse,
    requestToken
  } = useTransactionToken('my_profile', false, true);

  const [profile, setProfile] = useState<UserDetail | null>(null);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isRequestingToken, setIsRequestingToken] = useState(false);
  const [formData, setFormData] = useState({
    account: '',
    username: '',
    email: '',
    department: '',
    job_title: '',
    phone: ''
  });

  // Load profile on mount
  useEffect(() => {
    if (!permissionLoading && hasPermission('my_profile', 'read') && !hasInitialized.current) {
      hasInitialized.current = true;
      loadProfile();
    }
  }, [permissionLoading, hasPermission]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await getMyProfile();
      setProfile(data);
      setFormData({
        account: data.account || '',
        username: data.username || '',
        email: data.email || '',
        department: data.department || '',
        job_title: data.job_title || '',
        phone: data.phone || ''
      });

      // 載入組織名稱
      if (data.organization_id) {
        try {
          const org = await getOrganization(data.organization_id);
          setOrgName(org.org_name || String(data.organization_id));
        } catch {
          setOrgName(String(data.organization_id));
        }
      }

      // Log view
      await logRead('my_profile', data);
    } catch (error: any) {
      console.error('載入個人資料失敗:', error);
      showApiError(error, 'ERR020004', { operation: '載入個人資料', detail: error?.message || '' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    try {
      setIsRequestingToken(true);
      // Request transaction token first before entering edit mode
      await requestToken();

      // Wait a bit to ensure token is set
      await new Promise(resolve => setTimeout(resolve, 100));

      // Only enter edit mode after token is ready
      setIsEditMode(true);
    } catch (error) {
      console.error('取得交易令牌失敗:', error);
      showError('ERR020001', { entity: '交易令牌' });
    } finally {
      setIsRequestingToken(false);
    }
  };

  // §5.9：內嵌且 AI 帶入 prefill 時，載入後自動進入編輯模式並覆蓋對應欄位（只覆蓋允許欄位）
  const PREFILLABLE = ['username', 'department', 'job_title', 'phone'];
  useEffect(() => {
    // §5 修正5：只有讀取權限者不帶入、維持檢視模式（沒有修改權限才不進編輯）
    if (!embedded || prefillApplied.current || loading || !profile) return;
    if (!hasPermission('my_profile', 'update')) return;
    const entries = Object.entries(prefill || {}).filter(([k]) => PREFILLABLE.includes(k));
    if (entries.length === 0) return;
    prefillApplied.current = true;
    (async () => {
      await handleEdit();   // 取交易令牌 + 進入編輯模式
      setFormData(prev => ({ ...prev, ...Object.fromEntries(entries) }));
      setPrefilledFields(entries.map(([k]) => k));
      setDirty?.(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, loading, profile]);

  const handleCancel = () => {
    // Reset form to original values
    if (profile) {
      setFormData({
        account: profile.account || '',
        username: profile.username || '',
        email: profile.email || '',
        department: profile.department || '',
        job_title: profile.job_title || '',
        phone: profile.phone || ''
      });
    }
    setIsEditMode(false);
    setPrefilledFields([]);
    setDirty?.(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!txnToken) {
      showError('ERR020001', { entity: '交易令牌' });
      return;
    }

    try {
      const originalData = profile;
      const updatedProfile = await updateMyProfile(formData, txnToken);
      setProfile(updatedProfile);
      setIsEditMode(false);
      setPrefilledFields([]);
      setDirty?.(false);

      // Log update
      await logUpdate('my_profile', originalData as any, updatedProfile as any);

      showSuccess('SYS100001');
      if (embedded) pageExit();   // §5.9 內嵌：儲存成功後關閉 modal 回到對話
    } catch (error: any) {
      console.error('更新個人資料失敗:', error);
      const errorMsg = error.response?.data?.detail || error.message || t('myProfile.updateError', '更新個人資料失敗');

      // Log error
      await logUpdate('my_profile', profile as any, formData, errorMsg);

      showApiError(error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setDirty?.(true);   // §5.9 標記有未儲存變更（供 modal 關閉前確認）
  };

  // 內嵌時：被 AI 帶入的欄位加主題色外框 + 提示（§5.9·§6 修正6 改用 MUI theme primary.main）
  const primaryColor = theme.palette.primary.main;
  const prefilledStyle = (field: string): React.CSSProperties | undefined =>
    prefilledFields.includes(field)
      ? { outline: `2px solid ${primaryColor}`, outlineOffset: 1 }
      : undefined;
  const prefilledNote = (field: string) => (prefilledFields.includes(field) ? (
    <span className="field-note" style={{ color: primaryColor }}>
      {t('chat.modal.prefilled', '由對話帶入，請確認')}
    </span>
  ) : null);

  if (permissionLoading || loading) {
    return <div className="loading">{t('common.loading', '載入中...')}</div>;
  }

  if (!hasPermission('my_profile', 'read')) {
    return (
      <div className="no-permission">
        {t('common.noPermission', '您沒有權限訪問此頁面')}
      </div>
    );
  }

  const canUpdate = hasPermission('my_profile', 'update');

  return (
    <div className="my-profile-page">
      {!embedded && (
        <div className="page-header">
          <FunctionPageHeader funcCode="my_profile" />
        </div>
      )}

      <div className="profile-container">
        <form onSubmit={handleSubmit}>
          {/* Read-only fields */}
          <div className="form-section">
            <h3>{t('myProfile.accountInfo', '帳號資訊')}</h3>

            <div className="form-row">
              <label>{t('myProfile.organization', '組織')}</label>
              <input
                type="text"
                value={orgName || profile?.organization_id || ''}
                disabled
                className="readonly-input"
              />
              <span className="field-note">{t('myProfile.organizationNote', '組織無法變更')}</span>
            </div>
          </div>

          {/* Editable fields */}
          <div className="form-section">
            <h3>{t('myProfile.personalInfo', '個人資訊')}</h3>

            <div className="form-row">
              <label>{t('myProfile.account', '帳號')} *</label>
              <input
                type="text"
                value={formData.account}
                onChange={(e) => handleInputChange('account', e.target.value)}
                disabled={!isEditMode}
                required
                maxLength={100}
              />
              <span className="field-note">{t('myProfile.accountNote', '帳號必須唯一')}</span>
            </div>

            <div className="form-row">
              <label>{t('myProfile.username', '使用者名稱')} *</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                disabled={!isEditMode}
                required
                maxLength={200}
                style={prefilledStyle('username')}
              />
              {prefilledNote('username')}
            </div>

            <div className="form-row">
              <label>{t('users.email', '電子郵件')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={!isEditMode}
                maxLength={255}
                placeholder="name@example.com"
              />
              <span className="field-note">
                {t('myProfile.emailNote', '會議邀請、系統通知會寄到這個信箱，請自行維護正確')}
              </span>
            </div>

            <div className="form-row">
              <label>{t('myProfile.department', '部門')}</label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
                disabled={!isEditMode}
                maxLength={200}
                style={prefilledStyle('department')}
              />
              {prefilledNote('department')}
            </div>

            <div className="form-row">
              <label>{t('myProfile.jobTitle', '職稱')}</label>
              <input
                type="text"
                value={formData.job_title}
                onChange={(e) => handleInputChange('job_title', e.target.value)}
                disabled={!isEditMode}
                maxLength={200}
                style={prefilledStyle('job_title')}
              />
              {prefilledNote('job_title')}
            </div>

            <div className="form-row">
              <label>{t('myProfile.phone', '電話')}</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                disabled={!isEditMode}
                maxLength={200}
                style={prefilledStyle('phone')}
              />
              {prefilledNote('phone')}
            </div>
          </div>

          {/* Action buttons */}
          <div className="form-actions">
            {!isEditMode ? (
              <>
                <button
                  type="button"
                  onClick={pageExit}
                  className="btn-secondary"
                >
                  {embedded ? t('common.close', '關閉') : t('common.back', '返回')}
                </button>
                {canUpdate && (
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="btn-primary"
                    disabled={isRequestingToken}
                  >
                    {isRequestingToken ? t('common.loading', '處理中...') : t('common.edit', '編輯')}
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary"
                >
                  {t('common.cancel', '取消')}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={!txnToken}
                >
                  {t('common.save', '儲存')}
                </button>
              </>
            )}
          </div>
        </form>

        {/* LINE 綁定 / AI 用量：僅一般功能模式顯示（modal 內嵌聚焦編輯·§5.8）*/}
        {!embedded && <LineBindingSection />}
        {!embedded && <MyAiUsagePanel />}
      </div>

      {/* Transaction Extend Dialog */}
      <TransactionExtendDialog
        open={showExtendPrompt}
        remainingSeconds={remainingSeconds}
        onExtend={() => handleExtendResponse(true)}
        onCancel={() => handleExtendResponse(false)}
      />
    </div>
  );
};

export default MyProfilePage;
